import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { getFirebaseIdToken } from '../auth/firebase';
import {
    OwnerInvite,
    OwnerInviteInput,
    addOwnerInvite,
    assignInviteToLitter,
    clearInviteDepositStatus,
    clearInviteTags,
    deleteOwnerInvite,
    hasInviteForEmail,
    markPortalInvited,
    listOwnerInvites,
    upsertApplicationInvite,
    selectInboxInvites,
    selectOwnersForLitter,
    selectUnplannedLitterGroups,
    setInviteDepositStatus,
    setInviteTag,
    unassignInvite,
} from './inviteStore';
import {
    CreateInviteResult,
    ImportApplicationsResult,
    createRemoteInvite,
    deleteRemoteInvite,
    importRemoteApplications,
    listRemoteInvites,
    patchRemoteInvite,
    importRemoteApplication,
    sendRemotePortalInvite,
} from './inviteApi';
import { applicationDraftFromRecord } from './applicationFromRecord';
import { parseCsv } from './jotformCsv';
import { DepositStatus, GeneralInviteTag } from './inviteTags';

export const useAdminInvites = () => {
    const { user, firebaseReady } = useAuth();
    const localOnly = !firebaseReady || user?.email === 'dev-admin@local';
    const [invites, setInvites] = useState<OwnerInvite[]>(() =>
        localOnly ? listOwnerInvites() : []
    );
    const [loading, setLoading] = useState(!localOnly);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        if (localOnly) {
            setInvites(listOwnerInvites());
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const token = await getFirebaseIdToken();
            if (!token) {
                throw new Error('Sign in again to manage invites.');
            }
            setInvites(await listRemoteInvites(token));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load invites.');
        } finally {
            setLoading(false);
        }
    }, [localOnly]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const withRemote = useCallback(
        async <T,>(work: (token: string) => Promise<T>): Promise<T> => {
            const token = await getFirebaseIdToken();
            if (!token) {
                throw new Error('Sign in again to manage invites.');
            }
            const result = await work(token);
            await refresh();
            return result;
        },
        [refresh]
    );

    const create = useCallback(
        async (input: OwnerInviteInput): Promise<CreateInviteResult> => {
            if (localOnly) {
                if (hasInviteForEmail(input.email)) {
                    throw new Error('That email already has an invite on this browser.');
                }
                const invite = addOwnerInvite(input);
                markPortalInvited(invite.id);
                await refresh();
                return { invite, emailed: false };
            }
            return withRemote((token) => createRemoteInvite(token, input));
        },
        [localOnly, refresh, withRemote]
    );

    const setTag = useCallback(
        async (id: string, tag: GeneralInviteTag) => {
            if (localOnly) {
                setInviteTag(id, tag);
                await refresh();
                return;
            }
            await withRemote((token) => patchRemoteInvite(token, { id, tags: [tag] }));
        },
        [localOnly, refresh, withRemote]
    );

    const clearTags = useCallback(
        async (id: string) => {
            if (localOnly) {
                clearInviteTags(id);
                await refresh();
                return;
            }
            await withRemote((token) => patchRemoteInvite(token, { id, tags: [] }));
        },
        [localOnly, refresh, withRemote]
    );

    const setDeposit = useCallback(
        async (id: string, status: Exclude<DepositStatus, ''>) => {
            if (localOnly) {
                setInviteDepositStatus(id, status);
                await refresh();
                return;
            }
            await withRemote((token) => patchRemoteInvite(token, { id, depositStatus: status }));
        },
        [localOnly, refresh, withRemote]
    );

    const clearDeposit = useCallback(
        async (id: string) => {
            if (localOnly) {
                clearInviteDepositStatus(id);
                await refresh();
                return;
            }
            await withRemote((token) => patchRemoteInvite(token, { id, depositStatus: '' }));
        },
        [localOnly, refresh, withRemote]
    );

    const moveToLitter = useCallback(
        async (id: string, litterName: string) => {
            if (localOnly) {
                assignInviteToLitter(id, litterName);
                await refresh();
                return;
            }
            await withRemote((token) => patchRemoteInvite(token, { id, litter: litterName }));
        },
        [localOnly, refresh, withRemote]
    );

    const moveBack = useCallback(
        async (id: string) => {
            if (localOnly) {
                unassignInvite(id);
                await refresh();
                return;
            }
            await withRemote((token) => patchRemoteInvite(token, { id, litter: '' }));
        },
        [localOnly, refresh, withRemote]
    );

    const importApplications = useCallback(
        async (file: File): Promise<ImportApplicationsResult> => {
            const csvText =
                typeof file.text === 'function'
                    ? await file.text()
                    : await new Promise<string>((resolve, reject) => {
                          const reader = new FileReader();
                          reader.onload = () => resolve(String(reader.result || ''));
                          reader.onerror = () => reject(new Error('Could not read that CSV.'));
                          reader.readAsText(file);
                      });
            const rows = parseCsv(csvText);
            if (!rows.length) {
                throw new Error('That file does not look like a Jotform CSV export.');
            }
            if (localOnly) {
                let created = 0;
                let updated = 0;
                let skipped = 0;
                rows.forEach((row) => {
                    const draft = applicationDraftFromRecord(row, 'Jotform CSV');
                    if (!draft) {
                        skipped += 1;
                        return;
                    }
                    const result = upsertApplicationInvite(draft);
                    if (result.created) {
                        created += 1;
                    } else if (result.skipped) {
                        skipped += 1;
                    } else {
                        updated += 1;
                    }
                });
                await refresh();
                return { created, updated, skipped };
            }
            return withRemote((token) => importRemoteApplications(token, rows));
        },
        [localOnly, refresh, withRemote]
    );

    const sendPortalInvite = useCallback(
        async (id: string) => {
            if (localOnly) {
                throw new Error(
                    'Portal email is sent on the live site after you sign in with Google.'
                );
            }
            await withRemote((token) => sendRemotePortalInvite(token, id));
        },
        [localOnly, withRemote]
    );

    const uploadApplication = useCallback(
        async (id: string, file: File) => {
            if (localOnly) {
                throw new Error(
                    'Filling from a PDF is available on the live site after you sign in with Google.'
                );
            }
            const pdfBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = String(reader.result || '');
                    const comma = result.indexOf(',');
                    resolve(comma === -1 ? result : result.slice(comma + 1));
                };
                reader.onerror = () => reject(new Error('Could not read that PDF.'));
                reader.readAsDataURL(file);
            });
            await withRemote((token) =>
                importRemoteApplication(token, {
                    id,
                    fileName: file.name,
                    pdfBase64,
                })
            );
        },
        [localOnly, withRemote]
    );

    const remove = useCallback(
        async (id: string) => {
            if (localOnly) {
                deleteOwnerInvite(id);
                await refresh();
                return;
            }
            await withRemote((token) => deleteRemoteInvite(token, id));
        },
        [localOnly, refresh, withRemote]
    );

    const inbox = useMemo(() => selectInboxInvites(invites), [invites]);
    const unplannedLitters = useMemo(
        () => selectUnplannedLitterGroups(invites),
        [invites]
    );

    return {
        invites,
        inbox,
        unplannedLitters,
        ownersForLitter: (litterName: string) => selectOwnersForLitter(invites, litterName),
        localOnly,
        loading,
        error,
        create,
        setTag,
        clearTags,
        setDeposit,
        clearDeposit,
        moveToLitter,
        moveBack,
        importApplications,
        sendPortalInvite,
        uploadApplication,
        remove,
        refresh,
    };
};

export type AdminInviteActions = Pick<
    ReturnType<typeof useAdminInvites>,
    | 'setTag'
    | 'clearTags'
    | 'setDeposit'
    | 'clearDeposit'
    | 'uploadApplication'
    | 'sendPortalInvite'
    | 'remove'
>;
